/**
 * GP Ads AI - Alerts API Client
 * 
 * Client library for alert management endpoints.
 * Implements PRD-3593: Alert Dashboard
 */

import { apiClient } from './apiClient';

export interface Alert {
  alert_id: string;
  campaign_id: string;
  campaign_name: string;
  platform: 'meta' | 'google' | 'tiktok';
  alert_type: 'budget' | 'performance' | 'demographic' | 'predictive';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  benchmark: number;
  current_value: number;
  detected_at: string;
  status: 'active' | 'acknowledged' | 'resolved' | 'snoozed' | 'dismissed';
  priority_score: number;
  recommendations?: Recommendation[];
  acknowledged_at?: string;
  acknowledged_by?: string;
  resolved_at?: string;
  resolved_by?: string;
  action_taken?: string;
  dismissed_at?: string;
  dismissed_by?: string;
  dismiss_reason?: string;
}

export interface Recommendation {
  title: string;
  description: string;
  expected_impact: string;
  confidence: number;
  action: string;
  parameters?: Record<string, any>;
  reasoning?: string;
}

export interface AlertFilters {
  severity?: string;
  alert_type?: string;
  platform?: string;
  campaign_id?: string;
  status?: string;
}

export interface AlertStats {
  total_alerts: number;
  campaigns_affected: number;
  active_alerts: number;
  critical_alerts: number;
  avg_priority: number;
}

export interface SnoozeResponse {
  status: string;
  alert_id: string;
  snooze_until: string;
}

export interface DismissResponse {
  status: string;
  alert_id: string;
  undo_available: boolean;
  undo_expires_at: string;
  message: string;
}

export interface UndoDismissResponse {
  status: string;
  alert_id: string;
  message: string;
}

export interface SnoozeOptions {
  value: string;
  label: string;
  hours: number;
}

export const alertsApi = {
  /**
   * Get filtered alerts for organization
   */
  getAlerts: async (organizationId: string, filters: AlertFilters = {}): Promise<Alert[]> => {
    const params = new URLSearchParams({
      organization_id: organizationId,
      ...filters,
    });

    const response = await apiClient.get(`/alerts/?${params.toString()}`);
    return response.data;
  },

  /**
   * Get single alert with full details
   */
  getAlert: async (alertId: string, organizationId: string): Promise<Alert> => {
    const response = await apiClient.get(`/alerts/${alertId}?organization_id=${organizationId}`);
    return response.data;
  },

  /**
   * Get alert count for badge
   */
  getAlertCount: async (organizationId: string): Promise<number> => {
    const stats = await alertsApi.getStats(organizationId);
    return stats.active_alerts;
  },

  /**
   * Get alert statistics
   */
  getStats: async (organizationId: string): Promise<AlertStats> => {
    const response = await apiClient.get(`/alerts/stats/?organization_id=${organizationId}`);
    return response.data;
  },

  /**
   * Acknowledge alert
   */
  acknowledge: async (alertId: string, organizationId: string, userId: string): Promise<void> => {
    await apiClient.post(
      `/alerts/${alertId}/acknowledge?organization_id=${organizationId}&user_id=${userId}`
    );
  },

  /**
   * Resolve alert with action taken
   */
  resolve: async (
    alertId: string,
    organizationId: string,
    userId: string,
    actionTaken: string,
    notes?: string
  ): Promise<void> => {
    await apiClient.post(
      `/alerts/${alertId}/resolve?organization_id=${organizationId}&user_id=${userId}`,
      {
        action_taken: actionTaken,
        notes,
      }
    );
  },

  /**
   * Dismiss alert
   * PRD-3596: Returns undo_available and undo_expires_at
   */
  dismiss: async (
    alertId: string,
    organizationId: string,
    userId: string,
    reason: string
  ): Promise<DismissResponse> => {
    const response = await apiClient.post(
      `/alerts/${alertId}/dismiss?organization_id=${organizationId}&user_id=${userId}&reason=${encodeURIComponent(reason)}`
    );
    return response.data;
  },

  /**
   * Undo dismiss alert within 24-hour window
   * PRD-3596: Restores dismissed alert to active status
   */
  undoDismiss: async (
    alertId: string,
    organizationId: string,
    userId: string
  ): Promise<UndoDismissResponse> => {
    const response = await apiClient.post(
      `/alerts/${alertId}/undo-dismiss?organization_id=${organizationId}&user_id=${userId}`
    );
    return response.data;
  },

  /**
   * Snooze alert for duration
   * PRD-3596: Returns snooze_until timestamp
   */
  snooze: async (
    alertId: string,
    organizationId: string,
    userId: string,
    durationHours: number,
    reason?: string
  ): Promise<SnoozeResponse> => {
    const response = await apiClient.post(
      `/alerts/${alertId}/snooze?organization_id=${organizationId}&user_id=${userId}`,
      {
        duration_hours: durationHours,
        reason,
      }
    );
    return response.data;
  },

  /**
   * Get snooze options for UI
   * PRD-3596: Returns preset snooze durations
   */
  getSnoozeOptions: async (): Promise<SnoozeOptions[]> => {
    const response = await apiClient.get('/alerts/snooze-options/');
    return response.data;
  },

  /**
   * Get alert history
   */
  getHistory: async (
    organizationId: string,
    options: {
      campaignId?: string;
      startDate?: Date;
      endDate?: Date;
      limit?: number;
    } = {}
  ): Promise<Alert[]> => {
    const params = new URLSearchParams({
      organization_id: organizationId,
    });

    if (options.campaignId) params.append('campaign_id', options.campaignId);
    if (options.startDate) params.append('start_date', options.startDate.toISOString());
    if (options.endDate) params.append('end_date', options.endDate.toISOString());
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await apiClient.get(`/alerts/history/?${params.toString()}`);
    return response.data;
  },

  /**
   * Get alert trend data for charts
   * PRD-3597: Alert History Analytics
   */
  getTrend: async (organizationId: string, days: number = 30): Promise<TrendDataPoint[]> => {
    const params = new URLSearchParams({
      organization_id: organizationId,
      days: days.toString(),
    });
    const response = await apiClient.get(`/alerts/analytics/trend?${params.toString()}`);
    return response.data;
  },

  /**
   * Export alert history as CSV
   * PRD-3597: Alert History CSV Export
   */
  exportCSV: (organizationId: string, options: ExportOptions = {}): void => {
    const params = new URLSearchParams({
      organization_id: organizationId,
      days: (options.days || 90).toString(),
    });
    if (options.alertType) params.append('alert_type', options.alertType);
    if (options.campaignId) params.append('campaign_id', options.campaignId);
    if (options.startDate) params.append('start_date', options.startDate);
    if (options.endDate) params.append('end_date', options.endDate);
    
    // Trigger download by opening URL
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1';
    window.open(`${baseUrl}/alerts/history/export/csv?${params.toString()}`, '_blank');
  },
};

// New types for PRD-3597
export interface TrendDataPoint {
  date: string;
  total: number;
  budget_burn: number;
  underperformance: number;
  demographic_shift: number;
  opportunity: number;
}

export interface ExportOptions {
  days?: number;
  alertType?: string;
  campaignId?: string;
  startDate?: string;
  endDate?: string;
}
